import { Control, Controller, UseFormGetValues, UseFormReset } from "react-hook-form";
import { useEffect, useState } from "react";
import { TextField, IconButton, TableContainer, Table, TableHead, TableRow, TableCell, Typography, TableBody, Checkbox, Box, Grid2, FormControl } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import { rules } from "../../../utils/validation/Validation";
import fontWeight from "../../../styles/mui/fontWeight";
import colors from "../../../styles/colors/colors";
import { AuthPeriodTablecell, disciplineDivider, FiMinusCircleIcon, FiPlusCircleIcon, rightSideDivider, TableContainerStyle, TableHeaderStyle, TableHeaderStyle2, TextHeaderStartMedium, TotalTablecell, TotalVisitCount, VisitCountTablecell } from "../styles/style";
import { InformationText } from "../../../app/common/TextLabel";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { TextConstants } from "../../../constants/Patient";
import { Asterisk } from "../../../styles/common/style";
import { Slots } from "../../../models/Referral";
import { Discipline } from "../../../models/Master";
import { DisciplineTableHeader, Numbers } from "../../../constants/Referral";
import { MemberInfoData } from "../../../models/Patient";
import { DisciplineListPayload } from "../../../models/PhysicianDetails";
import { getValue } from "../../../utils";

interface SampleTableProps {
  slotsState: Slots;
  control?: Control<any>;
  disciplineListState: Discipline[];
  reset: UseFormReset<any>;
  getValues: UseFormGetValues<any>;
  stWarning: boolean;
  memberInfoDataState: MemberInfoData;
  isBcbsTn: boolean;
  disciplineStatusState: DisciplineListPayload;
}
  interface DisciplineRow {
    id: number;
    disciplineId: number;
    discipline: string;
    date: any;
    currentVisit: number;
    nextVisit: number;
    checked: boolean;
  }
  export const DisciplineTable: React.FC<SampleTableProps> = ({
    disciplineListState,
    slotsState,
    reset,
    getValues,
    stWarning,
    control,
    memberInfoDataState,
    isBcbsTn,
    disciplineStatusState
  }) => {
    const savedDisciplines = getValues("requestService.disciplines") || [];
    const getRowsFromState = () =>
      disciplineListState
      .filter((discipline) => discipline.disciplineId !== 8)
      .map((discipline, index) => {
        const saved = savedDisciplines.find(
        (defaultDiscipline: any) =>
          defaultDiscipline.disciplineId === discipline.disciplineId
        );

        return {
        id: index + Numbers.ONE,
        disciplineId: discipline.disciplineId,
        discipline: discipline.disciplineName,
        currentVisit: saved ? saved.currentVisit : Numbers.ZERO,
        nextVisit: saved ? saved.nextVisit : Numbers.ZERO,
        checked: !!saved,
        date: saved ? saved.date : null,
        } as DisciplineRow;
      });

    const [rows, setRows] = useState(getRowsFromState());

    useEffect(() => {
      setRows(getRowsFromState());
    }, [disciplineListState, savedDisciplines.length]);

    const saveRowsToForm = (updatedRows: typeof rows) => {
      const selected = updatedRows
        .filter((row) => row.checked)
        .map((row) => ({
          disciplineId: row.disciplineId,
          discipline: row.discipline,
          currentVisit: row.currentVisit,
          nextVisit: row.nextVisit,
          date: row.date,
        }));
      reset({
        ...getValues(),
        requestService: {
          ...getValues("requestService"),
          disciplines: selected,
        },
      });
    };

    const handleCheck = (idx: number) => {
      setRows((rows) => {
      const updatedRows = rows.map((row, index) =>
        index === idx
        ? {
          ...row,
          checked: !row.checked,
          }
        : row
      );
      saveRowsToForm(updatedRows);
      return updatedRows;
      });
    };

     const handleDateChange = (idx: number, date: any) => {
      setRows((rows) => {
      const updatedRows = rows.map((row, index) =>
        index === idx
        ? {
          ...row,
          date: date,
          }
        : row
      );
      saveRowsToForm(updatedRows);
      return updatedRows;
      });
    };

    const handleCounter = (
      idx: number,
      counter: "currentVisit" | "nextVisit",
      addRemove: number
    ) => {
      setRows((rows) => {
      const updatedRows = rows.map((row, index) =>
        index === idx && row.checked
        ? {
          ...row,
          [counter]: Math.min(9999, Math.max(Numbers.ZERO, row[counter] + addRemove)),
          }
        : row
      );
      saveRowsToForm(updatedRows);
      return updatedRows;
      });
    };

    const isNextPeriodDisabled =
      dayjs(slotsState?.nextStartDate).isSame("2026-12-31", "day") &&
      dayjs(slotsState?.nextEndDate).isSame("2026-12-31", "day");

    const visitInput = (
      row: DisciplineRow,
      idx: number,
      visitType: "currentVisit" | "nextVisit"
    ) => {
      const inputDisabled = !row.checked || (visitType === "nextVisit" && isNextPeriodDisabled);
      return (
        <>
            <IconButton
            onClick={() => handleCounter(idx, visitType, -1)}
            disabled={inputDisabled}
            sx={{ color: inputDisabled ? undefined : colors.primary.main }}
            >
            <FiMinusCircleIcon editable={!inputDisabled} />
            </IconButton>
          <TextField
            variant="standard"
            size="medium"
            inputProps={{
              min: Numbers.ZERO,
              max: 9999,
              maxLength: Numbers.Four,
              style: { textAlign: "center", width: 50 },
            }}
            value={row[visitType]}
            disabled={inputDisabled}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, "");
              if (value.length > Numbers.Four)
                value = value.slice(Numbers.ZERO, Numbers.Four);
              const numValue = Math.max(
                Numbers.ZERO,
                Number(value) || Numbers.ZERO
              );
              setRows((rows) => {
                const updatedRows = rows.map((rowUpdate, index) =>
                  index === idx && rowUpdate.checked
                    ? { ...rowUpdate, [visitType]: numValue }
                    : rowUpdate
                );
                saveRowsToForm(updatedRows);
                return updatedRows;
              });
            }}
            sx={{
              "& input": {
                textAlign: "center",
              },
            }}
          />
          <IconButton
            onClick={() => handleCounter(idx, visitType, Numbers.ONE)}
            disabled={inputDisabled}
          >
            <FiPlusCircleIcon editable={!inputDisabled} />
          </IconButton>
        </>
      );
    };

    
    const getDisciplineStatus = (discipline: DisciplineRow) => {
      const status = disciplineStatusState?.disciplineList?.some(
        (item: any) =>
          item.skill === discipline.discipline &&
          item.serviceStatus === "Approved" && discipline.checked
      );
      return status;
    };

    return (
      <Controller
        name="requestService.disciplines"
        control={control}
        defaultValue={[]}
        rules={{
          ...rules.discipline,
          validate: (value: any[]) => {
            const invalid = value.some(
              (discipline) =>
                discipline.currentVisit + discipline.nextVisit === Numbers.ZERO
            );
            if (invalid) {
              return "At least 1 visit count is required for each selected discipline.";
            }

            const minDate = getValue(slotsState, "currentStartDate")
              ? dayjs(getValue(slotsState, "currentStartDate"))
              : null;
            const maxDate = getValue(slotsState, "currentEndDate")
              ? dayjs(getValue(slotsState, "currentEndDate"))
              : null;
            if (isBcbsTn) {
              const selectedDate = value.some(
                (discipline) =>
                  discipline.date &&
                  ((minDate && discipline.date.isBefore(minDate, "day")) ||
                    (maxDate && discipline.date.isAfter(maxDate, "day")))
              );
              if (selectedDate) {
                return `Date must be between ${
                  minDate ? minDate.format("MM/DD/YYYY") : ""
                } and ${maxDate ? maxDate.format("MM/DD/YYYY") : ""}`;
              }
            }

            return true;
          },
        }}
        render={({ fieldState: { error } }) => {
          return (
            <>
              {error && errorText(error.message ?? "")}
              <TableContainer sx={TableContainerStyle}>
                <Table>
                  <TableHead>
                    <TableRow sx={TableHeaderStyle2}>
                      <TableCell align="center" />
                      <TableCell align="center" />
                      <TableCell align="center" sx={rightSideDivider}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={fontWeight[3]}
                          color={colors.fonts[29]}
                          sx={AuthPeriodTablecell}
                        >
                          Current Auth Period
                        </Typography>
                        {InformationText(
                          `${formatDate(
                            slotsState.currentStartDate,
                            DateFormats.MM_DD_YYYY
                          )} - ${formatDate(
                            slotsState.currentEndDate,
                            DateFormats.MM_DD_YYYY
                          )}`,
                          fontWeight[5],
                          TextHeaderStartMedium,
                          colors.black[1]
                        )}
                      </TableCell>
                      {isBcbsTn && (
                        <TableCell
                          align="center"
                          sx={{ ...rightSideDivider, maxWidth: 110 }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={fontWeight[3]}
                            color={colors.fonts[29]}
                            sx={AuthPeriodTablecell}
                          >
                            Date of Last Previously Approved Visit Used/To Be
                            Used.
                          </Typography>
                        </TableCell>
                      )}
                      {memberInfoDataState && !memberInfoDataState.isIngest && (
                        <TableCell align="center">
                          <Typography
                            variant="subtitle2"
                            fontWeight={fontWeight[3]}
                            color={colors.fonts[29]}
                            sx={AuthPeriodTablecell}
                          >
                            Next Auth Period
                          </Typography>
                          {InformationText(
                            `${formatDate(
                              slotsState.nextStartDate,
                              DateFormats.MM_DD_YYYY
                            )} - ${formatDate(
                              slotsState.nextEndDate,
                              DateFormats.MM_DD_YYYY
                            )}`,
                            fontWeight[5],
                            TextHeaderStartMedium,
                            colors.black[1]
                          )}
                        </TableCell>
                      )}

                      <TableCell align="center" />
                    </TableRow>
                    <TableRow sx={TableHeaderStyle}>
                      {headerTableCell(DisciplineTableHeader.SELECT)}
                      {headerTableCell(DisciplineTableHeader.DISCIPLINES)}
                      {headerTableCell(
                        DisciplineTableHeader.REQUESTED_NO_OF_VISITS
                      )}
                      {isBcbsTn && headerTableCell("")}
                      {memberInfoDataState &&
                        !memberInfoDataState.isIngest &&
                        headerTableCell(
                          DisciplineTableHeader.REQUESTED_NO_OF_VISITS
                        )}
                      {headerTableCell(DisciplineTableHeader.TOTAL)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => {
                      const isSelected = row.checked;
                      const totalVisits =
                        (row.currentVisit || Numbers.ZERO) +
                        (row.nextVisit || Numbers.ZERO);
                      const showRowError =
                        isSelected && totalVisits === Numbers.ZERO;
                      return (
                        <TableRow key={row.id}>
                          <TableCell
                            align="center"
                            sx={{
                              borderRight: "1px solid #E0E0E0",
                              verticalAlign: "top",
                            }}
                          >
                            <Checkbox
                              checked={row.checked}
                              onChange={() => handleCheck(idx)}
                            />
                          </TableCell>
                          <TableCell align="center" sx={disciplineDivider}>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                fontWeight={fontWeight[4]}
                                color={colors.black[16]}
                              >
                                {row.discipline}
                              </Typography>
                            </Box>
                            <Box>
                              {row.disciplineId === Numbers.Four &&
                                row.checked &&
                                stWarning && (
                                  <Typography
                                    variant="subtitle1"
                                    color={colors.red[90]}
                                    fontWeight={fontWeight[3]}
                                    lineHeight={"1.5rem"}
                                    align="left"
                                  >
                                    {TextConstants.DISCIPLINE_WARNING}
                                  </Typography>
                                )}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={rightSideDivider}>
                            {visitInput(row, idx, "currentVisit")}
                          </TableCell>
                          {isBcbsTn && (
                            <TableCell align="center" sx={rightSideDivider}>
                              <FormControl variant="standard">
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <DesktopDatePicker
                                    value={row.date ? row.date : null}
                                    onChange={(date) => {
                                      handleDateChange(idx, date);
                                    }}
                                    maxDate={
                                      getValue(slotsState, "currentEndDate")
                                        ? dayjs(
                                            getValue(
                                              slotsState,
                                              "currentEndDate"
                                            )
                                          ).subtract(1, "day")
                                        : undefined
                                    }
                                    minDate={
                                      getValue(slotsState, "currentStartDate")
                                        ? dayjs(
                                            getValue(
                                              slotsState,
                                              "currentStartDate"
                                            )
                                          )
                                        : undefined
                                    }
                                    disabled={
                                      getDisciplineStatus(row) === false
                                    }
                                    format="MM/DD/YYYY"
                                    slotProps={{
                                      textField: {
                                        variant: "standard",
                                        InputLabelProps: { shrink: true },
                                      },
                                    }}
                                  />
                                  {row.checked &&
                                    row.date &&
                                    (() => {
                                      const minDate = getValue(
                                        slotsState,
                                        "currentStartDate"
                                      )
                                        ? dayjs(
                                            getValue(
                                              slotsState,
                                              "currentStartDate"
                                            )
                                          )
                                        : null;
                                      const maxDate = getValue(
                                        slotsState,
                                        "currentEndDate"
                                      )
                                        ? dayjs(
                                          getValue(
                                            slotsState,
                                            "currentEndDate"
                                          )
                                          ).subtract(1, "day")
                                        : null;
                                      const selectedDate = row.date
                                        ? dayjs(row.date)
                                        : null;
                                      if (
                                        selectedDate &&
                                        ((minDate &&
                                          selectedDate.isBefore(
                                            minDate,
                                            "day"
                                          )) ||
                                          (maxDate &&
                                            selectedDate.isAfter(
                                              maxDate,
                                              "day"
                                            )))
                                      ) {
                                        return (
                                          <>
                                            {errorText(`Date must be between`)}
                                            {errorText(
                                              `${
                                                minDate
                                                  ? minDate.format("MM/DD/YYYY")
                                                  : ""
                                              } and ${
                                                maxDate
                                                  ? maxDate.format("MM/DD/YYYY")
                                                  : ""
                                              }`
                                            )}
                                          </>
                                        );
                                      }
                                      return null;
                                    })()}
                                </LocalizationProvider>
                              </FormControl>
                            </TableCell>
                          )}
                          {memberInfoDataState &&
                            !memberInfoDataState.isIngest && (
                              <TableCell align="center" sx={rightSideDivider}>
                                {visitInput(row, idx, "nextVisit")}
                              </TableCell>
                            )}
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ mx: 1 }}
                            >
                              {headerTableCell(
                                (row.currentVisit + row.nextVisit).toString(),
                                true
                              )}
                            </Typography>
                            <Grid2 container justifyContent="center">
                              {showRowError &&
                                errorText("At least 1 visit count is required")}
                            </Grid2>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          );
        }}
      />
    );
  };

const errorText = (errorMessage: string) => {
  return (
    <Typography
      variant="subtitle1"
      color={colors.red[90]}
      fontWeight={fontWeight[4]}
      pr={2}
      sx={Asterisk}
    >
      {errorMessage}
    </Typography>
  );
};

export const headerTableCell = (value: string, isTotal?: boolean) => {
  let cellStyle;

  if (value === DisciplineTableHeader.TOTAL) {
    cellStyle = TotalTablecell;
  } else if (isTotal) {
    cellStyle = { justifyContent: "center" };
  } else {
    cellStyle = { justifyContent: "center", ...rightSideDivider };
  }
  return (
    <>
      <TableCell sx={cellStyle}>
        <Typography
          variant="subtitle2"
          fontWeight={fontWeight[3]}
          color={colors.black[16]}
          sx={
            value === DisciplineTableHeader.TOTAL
              ? TotalVisitCount
              : VisitCountTablecell
          }
        >
          {value}
        </Typography>
      </TableCell>
    </>
  );
};
